import { useCallback, useEffect, useMemo, useRef } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import {
  patientsApi,
  useDiagnosesQuery,
  useUpdateDiagnosisMutation,
} from '@features/patients/store/api';
import { Box, Skeleton, Typography } from '@mui/material';
import { FeatureGates, useEventTracking, useFeatureGate } from '@services/statsig';
import { useAppDispatch } from '@store/hooks/core';
import { useModal } from '@store/hooks/useModal';
import { useSnackbar } from 'notistack';

import { ClinicalCardFrame } from '../components/ClinicalCardFrame';
import { DiagnosesCard } from '../components/DiagnosesCard';
import { mapDiagnosesToVM } from '../vm/mapClinicalToVM';

interface V1DiagnosesCardProps {
  patientId: string;
  /**
   * Fire the cardOpened impression on mount (default). Hosts that embed this
   * card inside another tracked surface (the context-bar edit modal, which
   * fires editModalOpened) pass false so one open isn't counted twice.
   */
  trackImpression?: boolean;
  /** 'embedded' hands the chrome and scroll to a dialog host. */
  variant?: 'card' | 'embedded';
  /** Embedded only: the dialog's close handler, rendered in the header. */
  onClose?: () => void;
  /** Drops the card's own title for hosts that already render one (the V3 section shell). */
  hideTitle?: boolean;
  canAdd?: boolean;
  canEdit?: boolean;
  surface?: 'patient_page_v1' | 'patient_page_v3' | 'context_bar' | 'encounter';
  tableMaxHeight?: number | string;
}

/** Undo handle returned by an optimistic RTK Query cache patch. */
interface CachePatch {
  undo: () => void;
}

/** Today as the YYYY-MM-DD the diagnosis date fields use. */
const todayIsoDate = (): string => new Date().toISOString().split('T')[0];

/**
 * Data glue for the redesigned Diagnoses card on Patient Page V3. Reads
 * and writes through the existing per-resource diagnosis CRUD (add/edit via
 * the shared AddDiagnosisModal); "Mark resolved" is the soft PATCH
 * `{ is_active: false, end_date: today }`, mirroring the legacy semantics
 * where a resolved diagnosis keeps its row.
 */
export const V1DiagnosesCard: React.FC<V1DiagnosesCardProps> = ({
  patientId,
  trackImpression = true,
  variant = 'card',
  onClose,
  hideTitle,
  canAdd = true,
  canEdit = true,
  surface = 'patient_page_v1',
  tableMaxHeight,
}) => {
  const { data, isLoading, isError } = useDiagnosesQuery({ patientId });
  const [updateDiagnosis] = useUpdateDiagnosisMutation();
  const dispatch = useAppDispatch();
  const { open } = useModal();

  // Optimistic cache patch so resolve/reactivate/suggestion actions reflect
  // instantly instead of waiting out mutation + refetch; reverted on error.
  const patchDiagnosis = useCallback(
    (
      diagnosisId: string,
      mutate: (row: { is_active?: boolean; end_date?: string | null; status: string }) => void,
    ) =>
      dispatch(
        patientsApi.util.updateQueryData(
          'getV2ProviderPatientsByPatientIdDiagnoses',
          { patientId },
          draft => {
            const target = draft.diagnoses.find(row => row.diagnosis_id === diagnosisId);
            if (target) mutate(target);
          },
        ),
      ) as unknown as CachePatch,
    [dispatch, patientId],
  );
  const { enqueueSnackbar } = useSnackbar();
  const { clinicalCards: tracking } = useEventTracking();
  // Default OFF → suggestions hidden; an org must opt in to see them.
  const showSuggestions = useFeatureGate(FeatureGates.SHOW_CHART_SUGGESTIONS);

  const diagnoses = useMemo(() => mapDiagnosesToVM(data?.diagnoses ?? []), [data]);
  const rowsById = useMemo(
    () => new Map((data?.diagnoses ?? []).map(row => [row.diagnosis_id, row])),
    [data],
  );

  const openedTracked = useRef(false);
  useEffect(() => {
    if (!trackImpression || openedTracked.current || !data) return;
    openedTracked.current = true;
    tracking.cardOpened({
      patient_id: patientId,
      item_type: 'diagnosis',
      item_count: diagnoses.length,
      surface,
    });
  }, [data, diagnoses.length, patientId, surface, tracking, trackImpression]);

  const handleAdd = useCallback(() => {
    tracking.addClicked({ patient_id: patientId, item_type: 'diagnosis', surface });
    open(MODAL_TYPES.ADD_DIAGNOSIS, { patientId });
  }, [open, patientId, surface, tracking]);

  const handleEdit = useCallback(
    (diagnosisId: string) => {
      tracking.editClicked({
        patient_id: patientId,
        item_type: 'diagnosis',
        item_id: diagnosisId,
        surface,
      });
      const row = rowsById.get(diagnosisId);
      open(MODAL_TYPES.ADD_DIAGNOSIS, {
        patientId,
        diagnosisId,
        defaultValues: row
          ? {
              name: row.name,
              code: row.code ?? undefined,
              date_added: row.date_added ?? undefined,
              end_date: row.end_date ?? undefined,
              is_active: row.is_active,
            }
          : undefined,
      });
    },
    [open, patientId, rowsById, surface, tracking],
  );

  const setActive = useCallback(
    (diagnosisId: string, toActive: boolean) => {
      tracking.statusToggled({
        patient_id: patientId,
        item_type: 'diagnosis',
        item_id: diagnosisId,
        to_active: toActive,
        surface,
      });
      const patch = patchDiagnosis(diagnosisId, row => {
        row.is_active = toActive;
        row.end_date = toActive ? null : todayIsoDate();
      });
      updateDiagnosis({
        patientId,
        diagnosisId,
        updateDiagnosisRequest: toActive
          ? { is_active: true, end_date: null }
          : { is_active: false, end_date: todayIsoDate() },
      })
        .unwrap()
        .catch(() => {
          patch.undo();
          enqueueSnackbar(
            toActive
              ? 'Could not reactivate the diagnosis. Try again.'
              : 'Could not resolve the diagnosis. Try again.',
            { variant: 'error' },
          );
        });
    },
    [enqueueSnackbar, patchDiagnosis, patientId, surface, tracking, updateDiagnosis],
  );

  const handleResolve = useCallback(
    (diagnosisId: string) => setActive(diagnosisId, false),
    [setActive],
  );
  const handleReactivate = useCallback(
    (diagnosisId: string) => setActive(diagnosisId, true),
    [setActive],
  );

  const resolveSuggestion = useCallback(
    (diagnosisId: string, action: 'accept' | 'dismiss') => {
      tracking.suggestionSelected({
        patient_id: patientId,
        item_type: 'diagnosis',
        item_id: diagnosisId,
        action,
        surface,
      });
      const patch = patchDiagnosis(diagnosisId, row => {
        row.status = action === 'accept' ? 'accepted' : 'rejected';
      });
      updateDiagnosis({
        patientId,
        diagnosisId,
        updateDiagnosisRequest: {
          status: action === 'accept' ? 'accepted' : 'rejected',
        },
      })
        .unwrap()
        .catch(() => {
          patch.undo();
          enqueueSnackbar(
            action === 'accept'
              ? 'Could not accept the suggestion. Try again.'
              : 'Could not dismiss the suggestion. Try again.',
            { variant: 'error' },
          );
        });
    },
    [enqueueSnackbar, patchDiagnosis, patientId, surface, tracking, updateDiagnosis],
  );

  const handleAcceptSuggestion = useCallback(
    (diagnosisId: string) => resolveSuggestion(diagnosisId, 'accept'),
    [resolveSuggestion],
  );
  const handleDismissSuggestion = useCallback(
    (diagnosisId: string) => resolveSuggestion(diagnosisId, 'dismiss'),
    [resolveSuggestion],
  );

  const handleFilterChanged = useCallback(
    (filter: 'all' | 'active' | 'inactive') => {
      tracking.filterChanged({ patient_id: patientId, item_type: 'diagnosis', filter, surface });
    },
    [patientId, surface, tracking],
  );

  const suggestedCount = useMemo(() => diagnoses.filter(dx => dx.isSuggested).length, [diagnoses]);

  const handleSuggestionsSectionToggled = useCallback(
    (expanded: boolean) => {
      tracking.suggestionsSectionToggled({
        patient_id: patientId,
        item_type: 'diagnosis',
        expanded,
        suggested_count: suggestedCount,
        surface,
      });
    },
    [patientId, suggestedCount, surface, tracking],
  );

  if (isLoading || !data) {
    return (
      <ClinicalCardFrame
        title="Diagnoses"
        hideTitle={hideTitle}
        variant={variant}
        onClose={onClose}
      >
        {isError ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, py: 4, textAlign: 'center' }}
          >
            Diagnoses could not be loaded. Refresh to try again.
          </Typography>
        ) : (
          <Box sx={{ px: 2, pb: 2 }}>
            {[0, 1, 2].map(row => (
              <Skeleton key={row} variant="rounded" height={44} sx={{ my: 1, borderRadius: 1 }} />
            ))}
          </Box>
        )}
      </ClinicalCardFrame>
    );
  }

  return (
    <DiagnosesCard
      variant={variant}
      onClose={onClose}
      hideTitle={hideTitle}
      diagnoses={diagnoses}
      onAdd={canAdd ? handleAdd : undefined}
      onEdit={canEdit ? handleEdit : undefined}
      onResolve={canEdit ? handleResolve : undefined}
      onReactivate={canEdit ? handleReactivate : undefined}
      onAcceptSuggestion={canEdit ? handleAcceptSuggestion : undefined}
      onDismissSuggestion={canEdit ? handleDismissSuggestion : undefined}
      onFilterChanged={handleFilterChanged}
      hideSuggestions={!showSuggestions}
      onSuggestionsSectionToggled={handleSuggestionsSectionToggled}
      tableMaxHeight={tableMaxHeight}
    />
  );
};
